import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Landmark, Scale, BookOpen, Shield, Users, Search, ExternalLink, Calendar, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link, useParams, Navigate } from "react-router-dom";
import { usePresentationsStore, getPresentationV2ForEntity } from "@/data/presentationsStore";
import { useEventsStore } from "@/data/eventsStore";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect } from "react";

type InstitutionData = {
  id: string;
  icon: React.ElementType;
  name: Record<string, string>;
  description: Record<string, string>;
  roles: Record<string, string[]>;
  website: string;
  composition?: Record<string, string>;
};

const institutionsData: InstitutionData[] = [
  {
    id: "parlement",
    icon: Landmark,
    name: { fr: "Parlement du Maroc", ar: "البرلمان المغربي", zgh: "ⴰⴱⵔⵍⴰⵎⴰⵏ ⵏ ⵍⵎⵖⵔⵉⴱ" },
    description: {
      fr: "Le Parlement marocain est composé de deux chambres : la Chambre des Représentants (395 membres élus au suffrage universel direct) et la Chambre des Conseillers (120 membres élus au suffrage indirect). Il exerce le pouvoir législatif, vote les lois et contrôle l'action du gouvernement.",
      ar: "يتكون البرلمان المغربي من مجلسين: مجلس النواب (395 عضواً منتخبين بالاقتراع العام المباشر) ومجلس المستشارين (120 عضواً منتخبين بالاقتراع غير المباشر). يمارس السلطة التشريعية ويصوت على القوانين ويراقب عمل الحكومة.",
      zgh: "ⴰⴱⵔⵍⴰⵎⴰⵏ ⴰⵎⵖⵔⴰⴱⵉ ⵉⴳⴰ ⵙⴳ ⵙⵉⵏ ⵉⵙⵇⵇⵉⵎⵏ: ⴰⵙⵇⵇⵉⵎ ⵏ ⵉⵎⵓⵔⴰ (395) ⴷ ⴰⵙⵇⵇⵉⵎ ⵏ ⵉⵎⵛⵛⵉⵡⴰⵕⵏ (120).",
    },
    roles: {
      fr: ["Voter les lois (pouvoir législatif)", "Contrôler l'action du gouvernement", "Évaluer les politiques publiques", "Voter le budget de l'État", "Ratifier les traités internationaux"],
      ar: ["التصويت على القوانين (السلطة التشريعية)", "مراقبة عمل الحكومة", "تقييم السياسات العمومية", "التصويت على ميزانية الدولة", "المصادقة على المعاهدات الدولية"],
      zgh: ["ⴰⵙⵜⵉ ⵏ ⵉⵙⵍⴳⴰⵏⵏ", "ⴰⵙⵏⵖⵎⵙ ⵏ ⵜⵏⴱⴰⴹⵜ", "ⴰⵙⵜⵉⵎⵔ ⵏ ⵜⵙⵔⵜⴰⵢ", "ⴰⵙⵜⵉ ⵏ ⵓⵎⵙⴰⵙⴰ", "ⴰⵇⴱⵍ ⵏ ⵉⵎⵢⴰⵇⵇⴰⵏⵏ"],
    },
    composition: { fr: "395 députés + 120 conseillers", ar: "395 نائب + 120 مستشار", zgh: "395 ⵉⵎⵓⵔⴰ + 120 ⵉⵎⵛⵛⵉⵡⴰⵕⵏ" },
    website: "https://www.parlement.ma",
  },
  {
    id: "cour-constitutionnelle",
    icon: Scale,
    name: { fr: "Cour Constitutionnelle", ar: "المحكمة الدستورية", zgh: "ⵜⴰⵙⵏⴱⴰⴹⵜ ⵜⴰⴷⵓⵙⵜⵓⵔⴰⵏⵜ" },
    description: {
      fr: "La Cour Constitutionnelle veille au respect de la Constitution. Elle contrôle la constitutionnalité des lois, statue sur les contestations électorales et garantit les droits fondamentaux des citoyens.",
      ar: "تسهر المحكمة الدستورية على احترام الدستور. تراقب دستورية القوانين وتبت في الطعون الانتخابية وتضمن الحقوق الأساسية للمواطنين.",
      zgh: "ⵜⴰⵙⵏⴱⴰⴹⵜ ⵜⴰⴷⵓⵙⵜⵓⵔⴰⵏⵜ ⵜⵙⵏⵖⵎⴰⵙ ⴰⵣⵔⴼ ⵏ ⵓⴷⵓⵙⵜⵓⵔ.",
    },
    roles: {
      fr: ["Contrôle de la constitutionnalité des lois", "Contentieux électoral", "Interprétation de la Constitution", "Protection des droits fondamentaux"],
      ar: ["مراقبة دستورية القوانين", "النزاعات الانتخابية", "تفسير الدستور", "حماية الحقوق الأساسية"],
      zgh: ["ⴰⵙⵏⵖⵎⵙ ⵏ ⵓⴷⵓⵙⵜⵓⵔ", "ⵉⵎⵏⵖⵉⵜⵏ ⵏ ⵜⵖⵜⵙⵉⵡⵉⵏ", "ⴰⵙⵙⴼⵔⵓ ⵏ ⵓⴷⵓⵙⵜⵓⵔ", "ⴰⵎⵃⴰⴷ ⵏ ⵉⵣⵔⴼⴰⵏ"],
    },
    website: "https://www.cour-constitutionnelle.ma",
  },
  {
    id: "cese",
    icon: BookOpen,
    name: { fr: "Conseil Économique, Social et Environnemental", ar: "المجلس الاقتصادي والاجتماعي والبيئي", zgh: "ⴰⵙⵇⵇⵉⵎ ⴰⴷⵎⵙⴰⵏ ⴷ ⵓⵏⴰⵎⵓⵏ ⴷ ⵓⵡⵏⵏⴰⴹ" },
    description: {
      fr: "Le CESE est une institution constitutionnelle consultative. Il émet des avis sur les orientations générales de l'économie nationale et du développement durable, et favorise le dialogue entre les partenaires économiques et sociaux.",
      ar: "المجلس الاقتصادي والاجتماعي والبيئي مؤسسة دستورية استشارية. يبدي رأيه في التوجهات العامة للاقتصاد الوطني والتنمية المستدامة ويشجع الحوار بين الشركاء.",
      zgh: "CESE ⵜⴳⴰ ⵜⵉⵎⵔⵙⵍⵜ ⵜⴰⴷⵓⵙⵜⵓⵔⴰⵏⵜ ⵏ ⵓⵎⵛⵉⵡⴰⵕ.",
    },
    roles: {
      fr: ["Avis sur les politiques économiques et sociales", "Études et rapports thématiques", "Dialogue social institutionnel", "Pétitions citoyennes"],
      ar: ["آراء حول السياسات الاقتصادية والاجتماعية", "دراسات وتقارير موضوعاتية", "الحوار الاجتماعي المؤسساتي", "العرائض المواطنة"],
      zgh: ["ⵉⵡⴰⵍⵉⵡⵏ ⵅⴼ ⵜⵙⵔⵜⴰⵢ", "ⵜⵉⵖⵓⵔⵉⵡⵉⵏ ⴷ ⵉⵏⵖⵎⵉⵙⵏ", "ⴰⵎⵙⴰⵡⴰⵍ ⴰⵏⴰⵎⵓⵏ", "ⵜⵉⵏⵎⵍⵉⵏ"],
    },
    website: "https://www.cese.ma",
  },
  {
    id: "cndh",
    icon: Shield,
    name: { fr: "Conseil National des Droits de l'Homme", ar: "المجلس الوطني لحقوق الإنسان", zgh: "ⴰⵙⵇⵇⵉⵎ ⴰⵏⴰⵎⵓⵔ ⵏ ⵉⵣⵔⴼⴰⵏ ⵏ ⵓⴼⴳⴰⵏ" },
    description: {
      fr: "Le CNDH est une institution nationale de promotion et de protection des droits de l'Homme. Il veille au respect des libertés et droits fondamentaux et contribue au renforcement de l'État de droit.",
      ar: "المجلس الوطني لحقوق الإنسان مؤسسة وطنية لتعزيز وحماية حقوق الإنسان. يسهر على احترام الحريات والحقوق الأساسية ويساهم في تعزيز دولة الحق والقانون.",
      zgh: "CNDH ⵜⴳⴰ ⵜⵉⵎⵔⵙⵍⵜ ⵏ ⵓⵎⵃⴰⴷ ⵏ ⵉⵣⵔⴼⴰⵏ ⵏ ⵓⴼⴳⴰⵏ.",
    },
    roles: {
      fr: ["Protection des droits fondamentaux", "Médiation et traitement des plaintes", "Observation des élections", "Promotion de la culture des droits de l'Homme"],
      ar: ["حماية الحقوق الأساسية", "الوساطة ومعالجة الشكايات", "ملاحظة الانتخابات", "نشر ثقافة حقوق الإنسان"],
      zgh: ["ⴰⵎⵃⴰⴷ ⵏ ⵉⵣⵔⴼⴰⵏ", "ⴰⵎⵙⴳⴳⵉⵡⵙ", "ⴰⵙⵏⵖⵎⵙ ⵏ ⵜⵖⵜⵙⵉⵡⵉⵏ", "ⴰⵙⵉⴷⴼ ⵏ ⵜⴷⵍⵙⴰ ⵏ ⵉⵣⵔⴼⴰⵏ"],
    },
    website: "https://www.cndh.org.ma",
  },
  {
    id: "mediateur",
    icon: Users,
    name: { fr: "Médiateur du Royaume", ar: "مؤسسة الوسيط", zgh: "ⴰⵎⵙⴳⴳⵉⵡⵙ ⵏ ⵜⴳⵍⴷⵉⵜ" },
    description: {
      fr: "Le Médiateur du Royaume est une institution indépendante qui intervient dans les relations entre l'administration et les usagers. Il reçoit les plaintes des citoyens et propose des solutions équitables.",
      ar: "مؤسسة الوسيط مؤسسة مستقلة تتدخل في العلاقات بين الإدارة والمرتفقين. تتلقى شكايات المواطنين وتقترح حلولاً منصفة.",
      zgh: "ⴰⵎⵙⴳⴳⵉⵡⵙ ⵏ ⵜⴳⵍⴷⵉⵜ ⵉⴳⴰ ⵜⵉⵎⵔⵙⵍⵜ ⵉⵙⵉⵎⴰⵏⵏ.",
    },
    roles: {
      fr: ["Médiation entre administration et citoyens", "Traitement des réclamations", "Recommandations aux administrations", "Amélioration des services publics"],
      ar: ["الوساطة بين الإدارة والمواطنين", "معالجة الشكايات", "توصيات للإدارات", "تحسين الخدمات العمومية"],
      zgh: ["ⴰⵎⵙⴳⴳⵉⵡⵙ ⴳⵔ ⵜⵏⴱⴰⴹⵜ ⴷ ⵉⵎⵣⴷⴰⵖⵏ", "ⴰⵙⵙⴼⵔⵓ ⵏ ⵜⵏⵎⵍⵉⵏ", "ⵉⵡⴰⵍⵉⵡⵏ", "ⴰⵙⵎⴰⵢⵏⵓ ⵏ ⵜⵏⴰⴼⵓⵜⵉⵏ"],
    },
    website: "https://www.mediateur.ma",
  },
  {
    id: "cour-comptes",
    icon: Search,
    name: { fr: "Cour des Comptes", ar: "المجلس الأعلى للحسابات", zgh: "ⵜⴰⵙⵏⴱⴰⴹⵜ ⵜⴰⵎⴰⵜⴰⵢⵜ ⵏ ⵉⵎⵉⴹⴰⵏⵏ" },
    description: {
      fr: "La Cour des Comptes est l'institution supérieure de contrôle des finances publiques. Elle vérifie la régularité et la sincérité des comptes publics et évalue la gestion des deniers publics.",
      ar: "المجلس الأعلى للحسابات هو المؤسسة العليا لمراقبة المالية العمومية. يتحقق من انتظام وصدق الحسابات العمومية ويقيم تدبير المال العام.",
      zgh: "ⵜⴰⵙⵏⴱⴰⴹⵜ ⵏ ⵉⵎⵉⴹⴰⵏⵏ ⵜⴳⴰ ⵜⵉⵎⵔⵙⵍⵜ ⵏ ⵓⵙⵏⵖⵎⵙ ⵏ ⵓⵥⵕⴼ ⴰⴳⴷⵓⴷⴰⵏ.",
    },
    roles: {
      fr: ["Contrôle des finances publiques", "Audit des comptes de l'État", "Évaluation de la gestion publique", "Publication de rapports annuels"],
      ar: ["مراقبة المالية العمومية", "تدقيق حسابات الدولة", "تقييم التدبير العمومي", "نشر التقارير السنوية"],
      zgh: ["ⴰⵙⵏⵖⵎⵙ ⵏ ⵓⵥⵕⴼ ⴰⴳⴷⵓⴷⴰⵏ", "ⴰⵙⴼⵜⵉ ⵏ ⵉⵎⵉⴹⴰⵏⵏ", "ⴰⵙⵜⵉⵎⵔ ⵏ ⵓⵙⵙⵓⴷⵙ", "ⴰⵙⵓⴼⵖ ⵏ ⵉⵏⵖⵎⵉⵙⵏ"],
    },
    website: "https://www.courdescomptes.ma",
  },
];

const InstitutionDetailPage = () => {
  const { lang, isRtl } = useI18n();
  const { id } = useParams<{ id: string }>();
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;
  const presentations = usePresentationsStore(state => state.presentations);

  const backLabel = { fr: "Retour à l'accueil", ar: "العودة للرئيسية", zgh: "ⵓⵖⴰⵍ ⵙ ⵓⵙⵏⵓⴱⴳ" };
  const rolesLabel = { fr: "Missions principales", ar: "المهام الرئيسية", zgh: "ⵜⵉⵎⵀⴰⵍ ⵜⵉⴷⵙⵍⴰⵏⵉⵏ" };
  const websiteLabel = { fr: "Site officiel", ar: "الموقع الرسمي", zgh: "ⴰⵙⵉⵜ ⵓⵏⵚⵉⴱ" };
  const compositionLabel = { fr: "Composition", ar: "التكوين", zgh: "ⴰⵙⴳⵓⵎ" };
  const eventsLabel = { fr: "Événements de l'institution", ar: "فعاليات المؤسسة", zgh: "ⵉⵎⵓⵙⵙⵓⵜⵏ ⵏ ⵜⵎⵔⵙⵍⵜ" };
  const noEventsLabel = { fr: "Aucun événement prévu pour le moment.", ar: "لا توجد فعاليات مبرمجة حاليا.", zgh: "ⵓⵔ ⵍⵍⵉⵏ ⵉⵎⵓⵙⵙⵓⵜⵏ ⵉⵜⵜⵓⵙⴽⴰⵔⵏ." };

  const inst = institutionsData.find(i => i.id === id);
  if (!inst) return <Navigate to="/" replace />;

  const v2Data = getPresentationV2ForEntity(inst.name.fr, "INSTITUTION");
  const displayDescription = v2Data?.description || inst.description[lang];
  const displayWebsite = v2Data?.website || inst.website;
  const displayLogoUrl = v2Data?.logoUrl;
  const hasV2Details = v2Data?.activites || v2Data?.contribution || v2Data?.projets || v2Data?.image1Url || v2Data?.image2Url;

  const { events, fetchEvents } = useEventsStore();
  
  useEffect(() => {
    fetchEvents(true);
  }, [fetchEvents]);

  const instEvents = events.filter(e => 
    e.status === "approved" && 
    (e.partyAcronym === inst?.name.fr || e.partyAcronym === inst?.id.toUpperCase() || e.partyAcronym === "Institution")
  );

  const Icon = inst.icon;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="container max-w-3xl">
          <Button variant="ghost" className="mb-8 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
            <Link to="/institutions">
              <BackArrow className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
              {backLabel[lang as keyof typeof backLabel]}
            </Link>
          </Button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-4 mb-6">
              {displayLogoUrl ? (
                <div className="w-16 h-16 shrink-0 flex items-center justify-center p-1 rounded-2xl bg-white shadow-sm border border-border">
                  <img src={displayLogoUrl} alt={`Logo ${inst.name[lang]}`} className="w-full h-full object-contain" />
                </div>
              ) : (
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary text-primary-foreground">
                  <Icon className="w-7 h-7" />
                </span>
              )}
              <h1 className="text-2xl md:text-3xl font-display font-extrabold text-foreground">{inst.name[lang]}</h1>
            </div>

            {inst.composition && (
              <Badge className="bg-primary/10 text-primary text-sm font-body px-4 py-2 mb-6">
                {compositionLabel[lang]}: {inst.composition[lang]}
              </Badge>
            )}
          </motion.div>

          <motion.div className="bg-card rounded-2xl border border-border shadow-card p-8 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <p className="text-foreground font-body leading-relaxed text-base mb-8 whitespace-pre-wrap">
              {displayDescription}
            </p>

            <h2 className="font-display font-bold text-lg text-foreground mb-4">{rolesLabel[lang]}</h2>
            <ul className="space-y-3">
              {(inst.roles[lang] || inst.roles.fr).map((role, i) => (
                <li key={i} className="flex items-start gap-3 text-sm font-body text-foreground">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  {role}
                </li>
              ))}
            </ul>
          </motion.div>

          {hasV2Details && (
            <div className="mb-8 grid gap-6 md:grid-cols-2">
              {v2Data?.activites && (
                <div className="bg-card p-6 rounded-2xl border border-border/50 shadow-sm">
                  <h4 className="font-display font-bold text-lg mb-2">Activités</h4>
                  <p className="text-muted-foreground whitespace-pre-wrap">{v2Data.activites}</p>
                </div>
              )}
              {v2Data?.contribution && (
                <div className="bg-card p-6 rounded-2xl border border-border/50 shadow-sm">
                  <h4 className="font-display font-bold text-lg mb-2">Contribution</h4>
                  <p className="text-muted-foreground whitespace-pre-wrap">{v2Data.contribution}</p>
                </div>
              )}
              {v2Data?.projets && (
                <div className="bg-card p-6 rounded-2xl border border-border/50 shadow-sm md:col-span-2">
                  <h4 className="font-display font-bold text-lg mb-2">Projets en cours / Perspectives</h4>
                  <p className="text-muted-foreground whitespace-pre-wrap">{v2Data.projets}</p>
                </div>
              )}
              {(v2Data?.image1Url || v2Data?.image2Url) && (
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {v2Data.image1Url && (
                    <div className="rounded-2xl overflow-hidden shadow-sm border border-border/50 h-64">
                      <img src={v2Data.image1Url} alt="Image représentative 1" className="w-full h-full object-cover" />
                    </div>
                  )}
                  {v2Data.image2Url && (
                    <div className="rounded-2xl overflow-hidden shadow-sm border border-border/50 h-64">
                      <img src={v2Data.image2Url} alt="Image représentative 2" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="text-center mb-12">
            <Button variant="outline" className="font-body font-semibold" asChild>
              <a href={displayWebsite} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />{websiteLabel[lang]}
              </a>
            </Button>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-display font-bold text-xl text-foreground mb-5 flex items-center gap-2 px-2">
              <Calendar className="w-5 h-5 text-primary" />{eventsLabel[lang]}
            </h2>
            {instEvents.length === 0 ? (
              <div className="bg-card rounded-3xl border border-border border-dashed p-8 text-center text-muted-foreground mb-8">
                 <Calendar className="w-10 h-10 mx-auto mb-3 opacity-20" />
                 <p className="font-body text-sm font-medium">{noEventsLabel[lang]}</p>
              </div>
            ) : (
              <div className="grid gap-4 mb-8">
                {instEvents.map(event => (
                  <Card key={event.id} className="shadow-sm hover:shadow-card transition-shadow border-border/80 rounded-2xl overflow-hidden group">
                    <CardContent className="p-5">
                      <Link to={`/evenement/${event.id}`} className="hover:text-primary transition-colors flex items-center justify-between mb-3">
                        <h3 className="font-display font-bold text-foreground text-md leading-tight max-w-[85%]">{event.title}</h3>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-transform rtl:hidden" />
                        <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:-translate-x-1 transition-transform rtl:block hidden" />
                      </Link>
                      <div className="flex flex-wrap items-center gap-4 text-xs mt-2 text-muted-foreground font-body bg-muted/40 p-2 rounded-lg w-max">
                        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-primary/70" />{event.date}</span>
                        <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary/70" />{event.location}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default InstitutionDetailPage;
